import type React from "react";

import { IconButton } from "@/components/Button/IconButton";
import { FormInput } from "@/components/Form/FormInput";
import { MinusIcon } from "@/components/Icons/MinusIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { useTranslation } from "@/translations";

import type { CreateOrEditAssetFormValues } from "../Layout";

const MIN_TIMESLOT = 5;
const MAX_TIMESLOT = 1440;
const STEP = 5;

interface AssetCustomTimeslotInputProps {
  value: number | undefined;
  onChange: (value: number) => void;
  isDisabled?: boolean;
}

export function AssetCustomTimeslotInput({
  value,
  isDisabled,
  onChange,
}: AssetCustomTimeslotInputProps): React.ReactNode {
  const { t } = useTranslation();

  const handleIncrease = () => {
    let newValue = Number(value || 0) + STEP;
    if (newValue % 5 != 0) {
      newValue = Math.floor(newValue / 5) * 5;
    }

    onChange(newValue);
  };

  const handleDecrease = () => {
    let newValue = Number(value || 10) - STEP;
    if (newValue % 5 != 0) {
      newValue = Math.floor(newValue / 5) * 5;
    }

    onChange(newValue);
  };

  return (
    <div className="flex flex-row items-start gap-2">
      <IconButton
        title={t("common.action.decrease")}
        icon={<MinusIcon />}
        disabled={(value != undefined && Number(value) <= MIN_TIMESLOT) || isDisabled}
        onClick={handleDecrease}
      />
      <FormInput<CreateOrEditAssetFormValues, "customTimeslot">
        name="customTimeslot"
        placeholder=""
        type="number"
        min={MIN_TIMESLOT}
        max={MAX_TIMESLOT}
        step={STEP}
        className="w-20"
        disabled={isDisabled}
        data-testid="custom-timeslot-input"
        postfix={<span className="text-caption">{t("common.date-part.minutes.short")}</span>}
        rules={{
          valueAsNumber: true,
          validate: {
            isValid(currValue) {
              if (!currValue) {
                return t("components.form.error.required", {
                  inputName: t("components.form.error.is-not-number"),
                });
              }

              if (currValue > MAX_TIMESLOT) {
                return t("components.form.error.max-value", {
                  value: MAX_TIMESLOT,
                });
              }

              if (currValue < MIN_TIMESLOT) {
                return t("components.form.error.min-value", {
                  value: MIN_TIMESLOT,
                });
              }

              if (currValue % 5 !== 0) {
                return t("components.form.error.must-be-increment-of", {
                  increment: 5,
                });
              }
            },
          },
        }}
      />
      <IconButton
        title={t("common.action.increase")}
        icon={<PlusIcon />}
        disabled={(value != undefined && Number(value) >= MAX_TIMESLOT) || isDisabled}
        onClick={handleIncrease}
      />
    </div>
  );
}
